import { GoogleLogin } from '@react-oauth/google'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/card/card'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'

import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { signInGoogleMutation } from '@/lib/queries/business/v1/auth/login/google/mutations'
import { signInMutation } from '@/lib/queries/business/v1/auth/login/mutations'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'
import { isApiErrorResponse, isWretchError } from '@/lib/types/predicate'
import { cn } from '@/lib/utils'

import { IndependentPageTopBar } from '../ui/individual-page-top-bar/individual-page-top-bar'

export const SignInPage = () => {
  const search = useSearch({
    from: '/sign-in',
  })
  const navigate = useNavigate()
  const mutation = useMutation(signInMutation)
  const mutationGoogle = useMutation(signInGoogleMutation)
  const form = useForm<{
    email: string
    password: string
  }>({
    defaultValues: { email: search.email ?? '', password: '' },
  })
  const { refetch } = useQuery(getUserQuery)

  const handleSubmit = form.handleSubmit((data) => {
    mutation.mutate(
      { email: data.email, password: data.password },
      {
        onSuccess: async (data) => {
          localStorage.setItem(LOCAL_STORAGE_KEY_MAP.token, data.token)
          await refetch()
          await navigate({ to: search.redirect ?? '/' })
        },
        onError: (err) => {
          if (isWretchError(err) && isApiErrorResponse(err.json)) {
            const json = err.json
            if (json?.code === 'LoginFailed') {
              toast.error('Please check your credentials and try again.')
            }
          }
        },
      }
    )
  })

  const { errors } = form.formState

  return (
    <>
      <IndependentPageTopBar />
      <div className={cn('mt-[150px]')}>
        <Form {...form}>
          <div className="flex flex-col space-y-6">
            <Card className="mx-auto w-[407px]">
              <CardHeader>
                <CardTitle className="leading-8">Log in</CardTitle>
                <CardDescription>Enter your email and password</CardDescription>
              </CardHeader>

              <CardContent>
                <div className="grid w-full items-center gap-4">
                  <div>
                    <GoogleLogin
                      width={360}
                      text="continue_with"
                      onSuccess={(credentialResponse) => {
                        mutationGoogle.mutate(
                          {
                            clientId: credentialResponse.clientId,
                            token: credentialResponse.credential,
                          },
                          {
                            onSuccess: async (data) => {
                              localStorage.setItem(LOCAL_STORAGE_KEY_MAP.token, data.token)
                              await refetch()
                              await navigate({ to: search.redirect ?? '/' })
                            },
                            onError: () => {
                              console.log('Login Failed')
                            },
                          }
                        )
                      }}
                      onError={() => {
                        console.log('Login Failed')
                      }}
                    />
                  </div>

                  <div className="flex items-center">
                    <div className="h-[1px] flex-1 bg-zinc-200" />
                    <div className="p-2.5 text-xs text-muted-foreground">OR CONTINUE WITH</div>
                    <div className="h-[1px] flex-1 bg-border" />
                  </div>

                  <Form {...form}>
                    <form className="grid w-full items-center gap-4">
                      {/* Input: email */}
                      <FormField
                        control={form.control}
                        rules={{ required: true }}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                autoFocus
                                className="placeholder:text-faint-foreground"
                                placeholder="email@example.com"
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {/* Input: password */}
                      <FormField
                        control={form.control}
                        rules={{ required: true }}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <Input className="placeholder:text-faint-foreground" type="password" {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {/* Submit */}
                      <div className="pt-2 text-center">
                        <Button
                          className="mb-2 w-full"
                          type="submit"
                          size="sm"
                          variant="default"
                          disabled={mutation.isPending || !!errors.email || !!errors.password}
                          onClick={handleSubmit}
                        >
                          Log in
                        </Button>
                        <Link className="mx-auto text-center text-xs text-primary" to="/forgot-password">
                          Forgot your password?
                        </Link>
                      </div>
                    </form>
                  </Form>
                </div>
              </CardContent>
            </Card>

            <div className="mx-auto max-w-[250px] text-center text-sm text-muted-foreground">
              By continuing, you agree to our
              <br />
              <Link className="border-b border-faint-foreground" to="/terms-of-service">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link className="border-b border-faint-foreground" to="/privacy-policy">
                Privacy Policy
              </Link>
              .
            </div>

            <div className="mx-auto">
              Don&apos;t have an account?{' '}
              <Link className="text-primary" to="/sign-up">
                Sign up
              </Link>
            </div>
          </div>
        </Form>
      </div>
    </>
  )
}
